
import React from 'react';
import { AgentType, NodeType } from '../types';
import { Play, Pause, RotateCcw, UserPlus, Stethoscope, HelpingHand, Skull, Car, ShieldAlert, Zap, Clock, Activity, BarChart3, Settings2, Power, FileBarChart } from 'lucide-react';
import { HUDButton, HUDPanel, HUDSlider } from './HUD/HUDPrimitives';
import { useAppContext } from '../contexts/AppContext';

export const SimulatePanel: React.FC<{ 
  onSpawnAgent: (type: AgentType) => void;
  onTriggerMCI: (mciType: 'EPIDEMIC' | 'ACCIDENT' | 'BIOHAZARD') => void;
}> = ({ onSpawnAgent, onTriggerMCI }) => {
  const { state, dispatch } = useAppContext();
  const { isRunning, isAutoSpawning, autoSpawnRate, simulationSpeed, viewMode, nodes, stats } = state;

  const hasEntrance = nodes.some(n => n.type === NodeType.ENTRANCE || n.type === NodeType.AMBULANCE_ENTRANCE);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <HUDPanel title="Valdymas" icon={<Activity size={12}/>} color={isRunning ? 'red' : 'emerald'}>
        <div className="flex gap-2">
          <HUDButton className="flex-1" variant={isRunning ? 'red' : 'emerald'} onClick={() => dispatch({ type: 'TOGGLE_RUN' })} icon={isRunning ? <Pause size={14}/> : <Play size={14}/>}>
            {isRunning ? 'Stabdyti' : 'Paleisti'}
          </HUDButton>
          <HUDButton onClick={() => dispatch({ type: 'RESET_SIMULATION' })} icon={<RotateCcw size={14}/>}/>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/5 space-y-4">
          <div className="flex items-center justify-between gap-3">
             <div className="flex flex-col">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Automatinis srautas</span>
                <span className="text-[8px] text-slate-600 uppercase font-mono">Generuoti pacientus periodiškai</span>
             </div>
             <HUDButton 
                active={isAutoSpawning} 
                variant={isAutoSpawning ? 'emerald' : 'slate'} 
                className="w-24 h-8"
                onClick={() => dispatch({ type: 'SET_AUTO_SPAWN', payload: { active: !isAutoSpawning } })}
                icon={<Power size={12}/>}
             >
                {isAutoSpawning ? 'Įjungta' : 'Išjungta'}
             </HUDButton>
          </div>

          {!hasEntrance && isAutoSpawning && (
            <div className="p-2 bg-red-500/10 border border-red-500/20 rounded flex items-start gap-2 animate-pulse">
              <ShieldAlert size={14} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-[8px] text-red-400 leading-tight uppercase font-bold">
                Klaida: Brėžinyje nėra įėjimo mazgų! Pacientai negali atsirasti.
              </p>
            </div>
          )}

          <HUDSlider label="Greitis" value={simulationSpeed} min={0.5} max={5} step={0.5} suffix="x" color="emerald" onChange={v => dispatch({ type: 'SET_SIM_SPEED', payload: v })} />
          <HUDSlider label="Srauto tankis" value={autoSpawnRate} min={0} max={300} suffix="/val" color="emerald" onChange={v => dispatch({ type: 'SET_AUTO_SPAWN', payload: { rate: v } })} />
        </div>
      </HUDPanel>

      <HUDPanel title="Analitika" icon={<FileBarChart size={12}/>} color="emerald">
          <HUDButton 
            className="w-full" 
            variant="emerald" 
            icon={<FileBarChart size={14}/>}
            disabled={stats.treatedPatients === 0 && stats.activePatients === 0}
            onClick={() => dispatch({ type: 'SET_REPORT_OPEN', payload: true })}
          >
            Generuoti Ataskaitą
          </HUDButton>
          <p className="mt-2 text-[8px] text-slate-600 uppercase font-mono text-center">Ataskaita paruošiama po pirmųjų pacientų apdorojimo.</p>
      </HUDPanel>

      <HUDPanel title="Konfigūracija" icon={<Settings2 size={12}/>}>
          <HUDButton 
            className="w-full" 
            variant="cyan" 
            icon={<Settings2 size={14}/>}
            onClick={() => dispatch({ type: 'SET_BEHAVIOR_MODAL_OPEN', payload: true })}
          >
            Elgsenos Protokolai
          </HUDButton>
      </HUDPanel>

      <HUDPanel title="Vizualizacija" icon={<BarChart3 size={12}/>}>
        <div className="grid grid-cols-3 gap-1">
          <HUDButton active={viewMode === 'NORMAL'} onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'NORMAL' })}>Std</HUDButton>
          <HUDButton active={viewMode === 'HEATMAP'} onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'HEATMAP' })}>HMap</HUDButton>
          <HUDButton active={viewMode === 'CONTAMINATION'} onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'CONTAMINATION' })}>Bio</HUDButton>
        </div>
      </HUDPanel>

      <HUDPanel title="Scenarijai" icon={<ShieldAlert size={12}/>} color="red">
        <div className="grid grid-cols-1 gap-2">
          <HUDButton variant="red" icon={<Skull size={14}/>} onClick={() => onTriggerMCI('EPIDEMIC')}>Epidemija</HUDButton>
          <HUDButton variant="red" icon={<Car size={14}/>} onClick={() => onTriggerMCI('ACCIDENT')}>Avarija</HUDButton>
        </div>
      </HUDPanel>

      <HUDPanel title="Agentai" icon={<UserPlus size={12}/>}>
        <div className="grid grid-cols-3 gap-2">
          <HUDButton icon={<Stethoscope size={14}/>} onClick={() => onSpawnAgent(AgentType.DOCTOR)}>GYD</HUDButton>
          <HUDButton icon={<Clock size={14}/>} onClick={() => onSpawnAgent(AgentType.NURSE)}>SLA</HUDButton>
          <HUDButton icon={<HelpingHand size={14}/>} onClick={() => onSpawnAgent(AgentType.NURSE_ASSISTANT)}>PAD</HUDButton>
        </div>
      </HUDPanel>
    </div>
  );
};

export default SimulatePanel;
